import React from 'react';
import { Loader2 } from 'lucide-react';
import type { Plan } from './types';
import { formatPagesPerSiteLabel, isUnlimitedPages } from './planChange';

export interface UpgradeConfirmModalProps {
  isOpen: boolean;
  plan: Plan | null;
  processing: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export const UpgradeConfirmModal: React.FC<UpgradeConfirmModalProps> = ({
  isOpen,
  plan,
  processing,
  onConfirm,
  onClose,
}) => {
  if (!isOpen || !plan) return null;

  const pageLabel = isUnlimitedPages(plan.maxPagesPerSite)
    ? 'Unlimited pages per site'
    : `Up to ${formatPagesPerSiteLabel(plan.maxPagesPerSite)} pages per site`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-md w-full mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm upgrade</h3>
        <p className="text-gray-600 mb-4">
          You are about to move to the <span className="font-semibold">{plan.displayName}</span>{' '}
          plan. Here is what is included:
        </p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1 text-sm">
          <li>
            Up to {plan.maxSites} {plan.maxSites === 1 ? 'site' : 'sites'}
          </li>
          <li>{pageLabel}</li>
          {plan.customDomain && <li>Custom domain support</li>}
          {plan.contactForms && <li>Contact forms</li>}
          {plan.advancedComponents && <li>Advanced components</li>}
          {plan.competeStudio && <li>Compete Studio</li>}
        </ul>
        {plan.features?.length > 0 && (
          <div className="mb-4 max-h-32 overflow-y-auto text-xs text-gray-600 border border-gray-100 rounded p-2">
            {plan.features.slice(0, 6).map((f, i) => (
              <p key={i} className="mb-0.5">
                — {f}
              </p>
            ))}
            {plan.features.length > 6 && (
              <p className="text-gray-400 mt-1">…and more on the plan card</p>
            )}
          </div>
        )}
        <div className="flex space-x-3">
          <button
            type="button"
            onClick={onConfirm}
            disabled={processing}
            className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {processing ? (
              <span className="inline-flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing…
              </span>
            ) : (
              'Continue'
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={processing}
            className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
